import { useState, useCallback } from 'react'
import {
  EContentSectionType,
  EMoveDirection,
  TMessageSection,
  TSectionPatch,
} from '../types'
import { createSection } from 'utils/messageSection'

export function useMessageSections(initialSections?: TMessageSection[]) {
  const [sections, setSections] = useState<TMessageSection[]>(
    initialSections ?? [createSection(EContentSectionType.TEXT)],
  )

  const handleAddSection = useCallback(() => {
    setSections((prev) => [...prev, createSection(EContentSectionType.TEXT)])
  }, [])

  const handlePatchSection = useCallback((id: string, patch: TSectionPatch) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== id) return section
        if (section.type === EContentSectionType.TEXT) {
          if (!('content' in patch)) return section
          return { ...section, content: patch.content }
        }
        if (section.type === EContentSectionType.FILE && 'content' in patch) {
          return { ...section, content: patch.content }
        }
        if (!('file' in patch)) return section
        return { ...section, file: patch.file }
      }),
    )
  }, [])

  const handleChangeType = useCallback((id: string, nextType: EContentSectionType) => {
    setSections((prev) =>
      prev.map((section) => (section.id === id ? createSection(nextType) : section)),
    )
  }, [])

  const handleDeleteSection = useCallback((id: string) => {
    setSections((prev) => prev.filter((section) => section.id !== id))
  }, [])

  const handleMoveSection = useCallback((id: string, direction: EMoveDirection) => {
    setSections((prev) => {
      const index = prev.findIndex((s) => s.id === id)
      if (index === -1) return prev
      const next = [...prev]
      if (direction === EMoveDirection.UP && index > 0) {
        [next[index - 1], next[index]] = [next[index], next[index - 1]]
      }
      if (direction === EMoveDirection.DOWN && index < next.length - 1) {
        [next[index], next[index + 1]] = [next[index + 1], next[index]]
      }
      return next
    })
  }, [])

  const handleSelectFile = useCallback((id: string, file: File) => {
    handlePatchSection(id, { file })
  }, [handlePatchSection])

  const handleUpdateSection = useCallback((id: string, updates: Partial<TMessageSection>) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== id) return section
        return { ...section, ...updates } as TMessageSection
      }),
    )
  }, [])

  return {
    sections,
    setSections,
    handleAddSection,
    handlePatchSection,
    handleChangeType,
    handleDeleteSection,
    handleMoveSection,
    handleSelectFile,
    handleUpdateSection,
  }
}
